<template>
  <div class="widget-wrapper cotizador-widget"
    :class="[wrapperClasses, { 'widget-wrapper-results': showResults, 'widget-wrapper-driver-form': showPersonalData }]"
    :style="widgetStyle">
    <div v-if="showBackground" class="widget-background" :style="backgroundStyle">
      <div class="background-overlay"></div>
    </div>
    <div ref="widgetContainerRef" class="widget-container"
      :class="[containerClasses, { 'widget-recotizing-active': showRecotizingOverlay, 'widget-container-driver-form': showPersonalData, 'widget-container-results': showResults }]">
      <!-- Wrapper interno para el overlay que cubre todo el contenido scrolleable -->
      <div class="widget-content-wrapper" :class="{ 'widget-content-loading': showRecotizingOverlay }">
        <!-- Overlay de loading local - siempre visible, sin scroll -->
        <!-- Debe estar al inicio del contenido para que sticky funcione -->
        <div v-if="showRecotizingOverlay" class="widget-recotizing-overlay" role="status" aria-live="polite">
          <div class="widget-recotizing-content">
            <div class="widget-recotizing-spinner-container">
              <div class="widget-recotizing-spinner widget-recotizing-spinner-primary"></div>
              <div class="widget-recotizing-spinner widget-recotizing-spinner-secondary"></div>
            </div>
            <div class="widget-recotizing-text">
              <p class="widget-recotizing-message">Obteniendo cotizaciones...</p>
              <p class="widget-recotizing-submessage">Consultando con las aseguradoras...</p>
            </div>
          </div>
        </div>

        <el-dialog
          :model-value="hasWidgetError"
          class="widget-error-dialog"
          :title="errorDialogTitle"
          width="min(420px, 92vw)"
          align-center
          append-to-body
          destroy-on-close
          :close-on-click-modal="true"
          :close-on-press-escape="true"
          @update:model-value="onErrorDialogVisibleChange"
        >
          <div class="widget-error-dialog-body">{{ translatedErrorMessage }}</div>
          <template #footer>
            <el-button type="primary" @click="handleCloseError">Entendido</el-button>
          </template>
        </el-dialog>

        <VehicleSelection v-if="!showPersonalData && !showResults"
          :year="quoteStore.quoteData.year ?? null"
          :brand="quoteStore.quoteData.brand ?? null"
          :type="quoteStore.quoteData.type ?? null"
          :model="quoteStore.quoteData.model ?? null"
          v-model:postal-code="formData.postalCode"
          v-model:postal-code-display="formData.postalCodeDisplay"
          :selected-year="selectedYear"
          :selected-brand-id="selectedBrandId"
          :selected-type-id="selectedTypeId"
          :selected-model-id="selectedModelId"
          :brands="vehicleData.brands.value"
          :tipos="vehicleData.tipos.value"
          :models="vehicleData.models.value"
          :is-loading-brands="vehicleData.isLoadingBrands.value"
          :is-loading-types="vehicleData.isLoadingTypes.value"
          :is-loading-models="vehicleData.isLoadingModels.value"
          :show-driver-step="needsDriverStep"
          @year-change="handleYearChange"
          @brand-change="handleBrandChange"
          @type-change="handleTypeChange"
          @model-change="handleModelChange"
          @postal-code-select="handlePostalCodeSelect"
          @continue="handleContinue" />

        <!-- Vista de resultados -->
        <ResultsView v-if="showResults || isDevMode" :quotes-response="quoteGeneration.quotesResponse.value"
          :is-loading="quoteGeneration.isLoading.value" :allow-emission="props.allowEmission ?? false"
          :quote-generation="quoteGeneration" @edit-vehicle="handleEditVehicleFromResults"
          @edit-driver="handleEditDriverFromResults" @quotes-updated="handleQuotesUpdated" />

        <!-- Vista de datos del conductor / contacto / demográficos Chubb -->
        <DriverDataForm v-else-if="showPersonalData" :form-data="formData" :is-loading="quoteGeneration.isLoading.value"
          :contact-only="props.xcf === true && !demographicsOnlyGate"
          :demographics-only="demographicsOnlyGate"
          :require-demographics="chubbEnabled && props.xcf === true"
          @update="handleFormUpdate" @back="handleChangeCar" @submit="handleGetQuote" />

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuoteStore } from '@/stores/quoteStore';
import { useVehicleData } from '@/composables/useVehicleData';
import { useQuoteGeneration } from '@/composables/useQuoteGeneration';
import { isChubbLegacyEnabled } from '@/config/chubbLegacy';
import VehicleSelection from '@/components/widget/VehicleSelection.vue';
import DriverDataForm from '@/components/widget/DriverDataForm.vue';
import ResultsView from '@/components/widget/ResultsView.vue';
import { seedDevMockData, useDevMode } from './cotizadorApp/devMock';
import { useDriverForm } from './cotizadorApp/useDriverForm';
import { useQuoteFlow } from './cotizadorApp/useQuoteFlow';
import { useVehicleSelection } from './cotizadorApp/useVehicleSelection';
import { useViewEffects } from './cotizadorApp/useViewEffects';
import { useWidgetAppearance } from './cotizadorApp/useWidgetAppearance';
import { useWidgetError } from './cotizadorApp/useWidgetError';

interface Props {
  showBackground?: boolean;
  backgroundImage?: string;
  size?: 'small' | 'medium' | 'large' | 'auto';
  fullHeight?: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  fontPrimary?: string;
  fontSecondary?: string;
  allowEmission?: boolean;
  /** Mount flag `xcf` — ver docs/ai/specs/widget-xcf-driver-contact.spec.md */
  xcf?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showBackground: true,
  backgroundImage: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80',
  size: 'auto',
  fullHeight: false,
  primaryColor: '#10b981',
  secondaryColor: '#059669',
  fontPrimary: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  fontSecondary: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  xcf: false,
});

const quoteStore = useQuoteStore();
const vehicleData = useVehicleData();
const quoteGeneration = useQuoteGeneration();

/** Chubb in the comparator (off by default; host opts in with enableChubb: true) — resolved at mount via entry.ts. */
const chubbEnabled = computed(() => isChubbLegacyEnabled());
/** Minimal gender+birth gate when Chubb on and no xcf contact step. */
const demographicsOnlyGate = ref(false);
const needsDriverStep = computed(() => props.xcf === true || chubbEnabled.value);

// Modo de desarrollo: mostrar ResultsView directamente con datos mock
const isDevMode = useDevMode();
if (isDevMode.value) {
  seedDevMockData(quoteGeneration, quoteStore);
}

const { wrapperClasses, containerClasses, backgroundStyle, widgetStyle } = useWidgetAppearance(props);

// Estado de vistas
const showPersonalData = ref(false);
const showResults = ref(false);
const widgetContainerRef = ref<HTMLElement | null>(null);

const {
  selectedYear,
  selectedBrandId,
  selectedTypeId,
  selectedModelId,
  handleYearChange,
  handleBrandChange,
  handleTypeChange,
  handleModelChange,
} = useVehicleSelection({ quoteStore, vehicleData, showPersonalData });

const { formData, fillFromStore, handleFormUpdate, handlePostalCodeSelect } = useDriverForm(
  quoteStore,
  showPersonalData,
);

const {
  handleContinue,
  handleGetQuote,
  handleChangeCar,
  handleEditVehicleFromResults,
  handleEditDriverFromResults,
  handleQuotesUpdated,
} = useQuoteFlow(
  { props, quoteStore, quoteGeneration, formData, chubbEnabled, showPersonalData, showResults, demographicsOnlyGate },
  fillFromStore,
);

const { translatedErrorMessage, hasWidgetError, errorDialogTitle, handleCloseError, onErrorDialogVisibleChange } =
  useWidgetError(quoteStore, quoteGeneration);

// Overlay de recotización: usa isFetchingQuotes de la instancia compartida con ResultsView
const showRecotizingOverlay = computed(() => quoteGeneration.isFetchingQuotes.value);

useViewEffects({ showResults, containerRef: widgetContainerRef, quoteGeneration });
</script>

<style scoped src="./cotizadorApp/CotizadorApp.css"></style>
